import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Route, Coins, Network, LineChart } from "lucide-react";
import { useLang } from "../context/LangContext";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";
import { Button } from "../components/ui/button";

const PILLAR_ICONS = [Route, LineChart, Network, Coins];

export default function Services() {
  const { t } = useLang();
  const pillars = [
    { title: t.services.pillar_1_title, sub: t.services.pillar_1_sub, items: t.services.pillar_1_items },
    { title: t.services.pillar_2_title, sub: t.services.pillar_2_sub, items: t.services.pillar_2_items },
    { title: t.services.pillar_3_title, sub: t.services.pillar_3_sub, items: t.services.pillar_3_items },
    { title: t.services.pillar_4_title, sub: t.services.pillar_4_sub, items: t.services.pillar_4_items },
  ];

  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="section-pad">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-5">
            <p className="eyebrow mb-5">{t.services.eyebrow}</p>
            <h1 className="display-heading text-4xl md:text-5xl lg:text-6xl accent-rule">{t.services.title}</h1>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-7">
            <p className="text-base md:text-lg text-text-muted leading-relaxed">{t.services.lead}</p>
          </FadeUp>
        </div>
      </section>

      {/* INDEX strip */}
      <section className="border-y border-rule bg-pearl">
        <div className="page-container py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <a
              key={i}
              href={`#pillar-${i + 1}`}
              data-testid={`pillar-anchor-${i + 1}`}
              className="group flex items-start gap-3 text-left hover:text-cerulean transition-colors"
            >
              <span className="pillar-num">P{String(i + 1).padStart(2, "0")}</span>
              <span className="text-xs md:text-sm text-ink group-hover:text-cerulean transition-colors leading-snug">
                {p.title}
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      {pillars.map((p, idx) => {
        const Icon = PILLAR_ICONS[idx];
        return (
          <section
            key={idx}
            id={`pillar-${idx + 1}`}
            className={`section-pad ${idx % 2 === 1 ? "bg-pearl" : "bg-white"}`}
          >
            <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
              <FadeUp className="lg:col-span-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center border border-ink">
                    <Icon className="w-5 h-5 text-ink" strokeWidth={1.4} />
                  </div>
                  <span className="pillar-num">PILAR {String(idx + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="display-heading text-3xl md:text-4xl accent-rule">{p.title}</h2>
                <p className="mt-8 text-sm font-mono tracking-[0.14em] uppercase text-cerulean">{p.sub}</p>
              </FadeUp>

              <Stagger className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                {p.items.map((it, j) => (
                  <StaggerItem key={j}>
                    <div className="bg-white border border-rule p-7 h-full mosaic-card">
                      <div className="text-[10px] font-mono tracking-[0.22em] text-cerulean">
                        {String(idx + 1)}.{String(j + 1).padStart(2, "0")}
                      </div>
                      <h3 className="mt-3 display-heading text-lg md:text-xl">{it.h}</h3>
                      <p className="mt-4 text-sm text-text-muted leading-relaxed">{it.d}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="section-pad bg-ink text-white">
        <div className="page-container grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="eyebrow text-cerulean">CONTACTO</p>
            <h2 className="display-heading text-white text-3xl md:text-4xl mt-4">
              {t.contact.title}
            </h2>
            <p className="mt-5 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed">{t.contact.lead}</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contacto" data-testid="services-cta">
              <Button className="bg-white text-ink hover:bg-cerulean hover:text-white rounded-none h-12 px-7 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors">
                {t.nav.cta} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
